import type { Locale } from "@/lib/i18n";

export const founders = [
  {
    id: "jazz-tong",
    name: "Jazz Tong",
    jobTitle: "Co-Founder & CEO",
    linkedIn: "https://www.linkedin.com/in/jazztong/",
    medium: "https://medium.com/@jazz-twk",
    portrait: "/images/people/jazz-tong.webp",
    portraitSize: 400,
    previousEmployers: ["FairPrice Group", "Kerry Logistics", "NE Digital", "Tigerspike", "Maxis"],
    credentials: ["Claude Certified Architect — Foundations", "AWS Certified Solutions Architect — Professional", "Certified ScrumMaster"],
  },
  {
    id: "liew-jee-fong",
    name: "Liew Jee Fong",
    jobTitle: "Co-Founder & Chief Business Officer",
    linkedIn: "https://www.linkedin.com/in/liew-jee-fong-b3099649/",
    portrait: "/images/people/liew-jee-fong.webp",
    portraitSize: 800,
    previousEmployers: ["CIMB", "Hong Leong Bank", "RHB", "OCBC", "Maybank"],
    credentials: ["Claude Certified Associate", "Digital & AI Evolution in Banking — Chartered Banker Institute", "HRD Corp accredited trainer"],
  },
] as const;

export const founderWriting = [
  {
    title: "How Malaysian Businesses Can Benefit From AI",
    href: "https://jazz-twk.medium.com/how-malaysian-businesses-can-benefit-from-ai-3938ea791939",
  },
  {
    title: "Agents Don’t Need Better Models. They Need Better Runbooks.",
    href: "https://jazz-twk.medium.com/agents-dont-need-better-models-they-need-better-runbooks-888f91d920b8",
  },
  {
    title: "The Rise of Vibe Coding: Why Developers Are Becoming Product Engineers",
    href: "https://jazz-twk.medium.com/the-rise-of-vibe-coding-why-developers-are-becoming-product-engineers-170fc3557c83",
  },
] as const;

interface PeoplePageCopy {
  eyebrow: string;
  heading: string;
  intro: string;
  storyHeading: string;
  story: string;
  proofHeading: string;
  proof: { label: string; detail: string; href?: string; englishOnly?: boolean }[];
  contact: string;
  linkedIn: string;
  focus: string;
  qualifications: string;
  profiles: {
    role: string;
    summary: string;
    paragraphs: string[];
    topics: string[];
  }[];
  medium: string;
  contributionsHeading: string;
  writingHeading: string;
  writingDescriptions: string[];
  appearancesHeading: string;
  approachEyebrow: string;
  approachHeading: string;
  readingTitle: string;
  readingDescription: string;
  academyTitle: string;
  academyDescription: string;
}

// Source record: docs/research/anchor-sprint-founders-2026-10-17.md.
// Company partner status and team certifications are not personal credentials.
export const peoplePageCopy: Record<Locale, PeoplePageCopy> = {
  en: {
    eyebrow: "Our people",
    heading: "Meet our co-founders.",
    intro: "18 years in software engineering. 15+ years in banking.",
    storyHeading: "Engineering and business, together.",
    story: "Founded in October 2023, Anchor Sprint brings enterprise engineering and practical AI to businesses in Malaysia and Singapore. Jazz and Jee Fong combine experience building and operating platforms with an understanding of how businesses finance, adopt and manage change.",
    contact: "Talk to our team",
    linkedIn: "LinkedIn",
    focus: "Focus areas",
    qualifications: "Qualifications",
    profiles: [
      {
        role: "Co-Founder & CEO",
        summary: "Software engineering, agent engineering and reliable systems.",
        paragraphs: [
          "Jazz has built software for 18 years. During four and a half years at FairPrice Group in Singapore, he served as Architecture & Reliability Lead, then Head of Platform Engineering until August 2026. His organisation covered reliability, developer experience, quality engineering, application security and AgentOps.",
          "His work included engineering quality metrics, DevSecOps, enterprise identity and single sign-on, Google Cloud agent-platform architecture and early AgentOps principles for production agents. The platform team also supported the group’s Agent Centre of Excellence.",
          "Before FairPrice, he worked at Maxis, Tigerspike, NE Digital and Kerry Logistics. At Kerry Logistics, he led and scaled the software engineering team to more than 90 people.",
          "Jazz became Anchor Sprint’s full-time CEO in September 2026. He leads production AI agents, data integration and custom software delivery, and writes on Medium about AgentOps, service catalogues, AI-native platform teams and practical AI adoption.",
          "Across his engineering career, Jazz has built systems serving 10 million monthly active users, managed more than 1,000 production servers and handled more than 200 incidents.",
        ],
        topics: ["Software Engineering", "Agent Engineering", "Platform Engineering", "AgentOps", "DevSecOps"],
      },
      {
        role: "Co-Founder & Chief Business Officer",
        summary: "Banking, business evaluation and practical AI adoption.",
        paragraphs: [
          "Jee Fong brings more than 15 years in corporate and commercial banking and business transformation. Her career includes Maybank, OCBC, RHB and leadership roles at Hong Leong Bank and CIMB. At Hong Leong she led a team of 12; her most recent banking role was Team Manager, Commercial Banking at CIMB.",
          "At Anchor Sprint, she leads business development, client partnerships and Anchor Academy’s AI training programmes. Her work includes developing technology partnerships with providers such as Lenovo, Adobe and Microsoft, signing an MOU with Tunku Abdul Rahman University of Management and Technology (TAR UMT), and securing the company’s Malaysia Digital status.",
          "An LSE alumna and HRD Corp accredited trainer, she contributes to AI and productivity panels, mentors student entrepreneurs and judges business pitches. Her engagements include HELP University, Taylor’s University, MMU Cyberjaya and INTI Nilai, alongside AI Build & Interiors Connect.",
          "Across her banking career, Jee Fong has reviewed RM1 billion in capital funding and evaluated more than 500 businesses.",
        ],
        topics: ["Business Evaluation", "Funding Assessment", "Corporate & Commercial Banking", "Business Partnerships", "AI Training & Adoption", "Technology Sourcing"],
      },
    ],
    medium: "Read Jazz on Medium",
    contributionsHeading: "Writing, panels and mentorship.",
    writingHeading: "Selected writing by Jazz",
    writingDescriptions: [
      "Why training needs changes to the workflow, and how to measure whether those changes help.",
      "A look at the instructions and checks around an agent, and why they matter to its performance.",
      "How AI-assisted coding changes the developer’s work, from implementation to product decisions.",
    ],
    appearancesHeading: "Jee Fong in the community",
    proofHeading: "Company credentials and partnerships.",
    proof: [
      { label: "Company", detail: "Founded October 2023 · Serving Malaysia and Singapore" },
      { label: "Malaysia Digital", detail: "Malaysia Digital status granted by MDEC", href: "/about-us/" },
      { label: "AI partnerships", detail: "Certified in the Claude Partner Network · OpenAI Select Partner", href: "/our-partners/", englishOnly: true },
      { label: "Technology supply", detail: "Authorised reseller: Lenovo, ASUS, Dell, Adobe, Microsoft and Red Hat", href: "/enterprise/" },
      { label: "University collaboration", detail: "MOU with TAR UMT" },
      { label: "Training", detail: "AI training programmes through Anchor Academy", href: "/academy/" },
    ],
    approachEyebrow: "Get to know",
    approachHeading: "Our approach.",
    readingTitle: "Why AI training alone is not enough",
    readingDescription: "Read our thinking on capability and workflow redesign.",
    academyTitle: "Learn with Anchor Academy",
    academyDescription: "Explore practical training for business teams.",
  },
  ms: {
    eyebrow: "Pasukan kami",
    heading: "Kenali pengasas bersama kami.",
    intro: "18 tahun dalam kejuruteraan perisian. Lebih 15 tahun dalam perbankan.",
    storyHeading: "Kejuruteraan dan perniagaan, bersama.",
    story: "Ditubuhkan pada Oktober 2023, Anchor Sprint membawa kejuruteraan perusahaan dan AI praktikal kepada perniagaan di Malaysia serta Singapura. Jazz dan Jee Fong menggabungkan pengalaman membina dan mengendalikan platform dengan pemahaman tentang pembiayaan, penerapan teknologi dan pengurusan perubahan dalam perniagaan.",
    contact: "Bincang dengan pasukan kami",
    linkedIn: "LinkedIn",
    focus: "Bidang tumpuan",
    qualifications: "Kelayakan",
    profiles: [
      {
        role: "Pengasas Bersama & CEO",
        summary: "Kejuruteraan perisian, kejuruteraan ejen dan sistem yang andal.",
        paragraphs: [
          "Jazz mempunyai 18 tahun pengalaman kejuruteraan perisian. Sepanjang empat setengah tahun di FairPrice Group, Singapura, beliau menjadi Ketua Seni Bina dan Kebolehpercayaan, kemudian Ketua Kejuruteraan Platform sehingga Ogos 2026. Organisasinya merangkumi kebolehpercayaan, pengalaman pembangun, kejuruteraan kualiti, keselamatan aplikasi dan AgentOps.",
          "Kerjanya merangkumi metrik kualiti kejuruteraan, DevSecOps, identiti perusahaan dan log masuk tunggal (SSO), seni bina platform ejen di Google Cloud serta prinsip awal AgentOps untuk ejen dalam operasi sebenar. Pasukan platform turut menyokong Agent Centre of Excellence kumpulan tersebut.",
          "Sebelum FairPrice, beliau berkhidmat di Maxis, Tigerspike, NE Digital dan Kerry Logistics. Di Kerry Logistics, beliau memimpin dan mengembangkan pasukan kejuruteraan perisian kepada lebih 90 orang.",
          "Jazz menjadi CEO sepenuh masa Anchor Sprint pada September 2026. Beliau mengetuai pembangunan ejen AI untuk operasi sebenar, penyepaduan data dan perisian tersuai, serta menulis di Medium tentang AgentOps, katalog perkhidmatan, pasukan platform berteraskan AI dan penerapan AI secara praktikal.",
          "Sepanjang kerjaya kejuruteraannya, Jazz membina sistem yang melayani 10 juta pengguna aktif bulanan, mengurus lebih 1,000 pelayan produksi dan menangani lebih 200 insiden.",
        ],
        topics: ["Kejuruteraan Perisian", "Kejuruteraan Ejen", "Kejuruteraan Platform", "AgentOps", "DevSecOps"],
      },
      {
        role: "Pengasas Bersama & Ketua Pegawai Perniagaan",
        summary: "Perbankan, penilaian perniagaan dan penerapan AI praktikal.",
        paragraphs: [
          "Jee Fong membawa lebih 15 tahun pengalaman dalam perbankan korporat dan komersial serta transformasi perniagaan. Kerjayanya merangkumi Maybank, OCBC, RHB dan jawatan kepimpinan di Hong Leong Bank serta CIMB. Di Hong Leong beliau memimpin pasukan seramai 12 orang; jawatan terakhirnya dalam perbankan ialah Pengurus Pasukan Perbankan Komersial di CIMB.",
          "Di Anchor Sprint, beliau mengetuai pembangunan perniagaan, perkongsian pelanggan dan program latihan AI Anchor Academy. Kerjanya merangkumi pembangunan kerjasama dengan penyedia teknologi seperti Lenovo, Adobe dan Microsoft, pemeteraian MOU dengan Tunku Abdul Rahman University of Management and Technology (TAR UMT), serta usaha mendapatkan status Malaysia Digital syarikat.",
          "Sebagai alumni LSE dan jurulatih bertauliah HRD Corp, beliau menyertai panel AI dan produktiviti, membimbing usahawan pelajar dan menilai pembentangan perniagaan. Penglibatannya merangkumi HELP University, Taylor’s University, MMU Cyberjaya dan INTI Nilai, serta AI Build & Interiors Connect.",
          "Sepanjang kerjaya perbankannya, Jee Fong menyemak pembiayaan modal bernilai RM1 bilion dan menilai lebih 500 perniagaan.",
        ],
        topics: ["Penilaian Perniagaan", "Penilaian Pembiayaan", "Perbankan Korporat & Komersial", "Perkongsian Perniagaan", "Latihan & Penerapan AI", "Perolehan Teknologi"],
      },
    ],
    medium: "Baca tulisan Jazz di Medium",
    contributionsHeading: "Penulisan, panel dan bimbingan.",
    writingHeading: "Daripada catatan Jazz",
    writingDescriptions: [
      "Mengapa latihan perlu disertai perubahan aliran kerja, serta cara mengukur hasilnya.",
      "Peranan arahan dan semakan dalam prestasi ejen AI.",
      "Bagaimana pengekodan berbantu AI mengubah kerja pembangun, daripada pelaksanaan kepada keputusan produk.",
    ],
    appearancesHeading: "Jee Fong bersama komuniti",
    proofHeading: "Kelayakan dan perkongsian syarikat.",
    proof: [
      { label: "Syarikat", detail: "Ditubuhkan Oktober 2023 · Malaysia dan Singapura" },
      { label: "Malaysia Digital", detail: "Status Malaysia Digital dianugerahkan oleh MDEC", href: "/about-us/" },
      { label: "Perkongsian AI", detail: "Certified in the Claude Partner Network · OpenAI Select Partner", href: "/our-partners/", englishOnly: true },
      { label: "Pembekalan teknologi", detail: "Penjual semula sah: Lenovo, ASUS, Dell, Adobe, Microsoft dan Red Hat", href: "/enterprise/" },
      { label: "Kerjasama universiti", detail: "MOU dengan TAR UMT" },
      { label: "Latihan", detail: "Program latihan AI melalui Anchor Academy", href: "/academy/" },
    ],
    approachEyebrow: "Kenali",
    approachHeading: "Pendekatan kami.",
    readingTitle: "Mengapa latihan AI sahaja tidak mencukupi",
    readingDescription: "Baca pandangan kami tentang keupayaan dan reka bentuk semula aliran kerja.",
    academyTitle: "Belajar bersama Anchor Academy",
    academyDescription: "Terokai latihan praktikal untuk pasukan perniagaan.",
  },
  zh: {
    eyebrow: "我们的团队",
    heading: "认识我们的联合创始人。",
    intro: "十八年软件工程经验，超过十五年银行业经验。",
    storyHeading: "工程经验与商业视角，汇聚于此。",
    story: "安克极速成立于 2023 年 10 月，为马来西亚和新加坡企业提供企业级工程服务和实用 AI。Jazz 与 Jee Fong 将构建及运行平台的经验，与企业融资、技术应用和变革管理的经验结合起来。",
    contact: "与我们的团队交流",
    linkedIn: "LinkedIn",
    focus: "关注领域",
    qualifications: "专业资质",
    profiles: [
      {
        role: "联合创始人兼首席执行官",
        summary: "软件工程、智能体工程与可靠系统。",
        paragraphs: [
          "Jazz 拥有十八年软件开发和工程团队管理经验。他在新加坡 FairPrice Group 工作四年半，先任架构与可靠性负责人，之后担任平台工程负责人，直至 2026 年 8 月。他的职责涵盖可靠性、开发者体验、质量工程、应用安全和 AgentOps。",
          "他参与的工作包括可靠性实践与工程质量指标、DevSecOps、企业身份管理与单点登录，以及 Google Cloud 上的智能体 AI 平台架构。他也制定了生产环境智能体的早期 AgentOps 原则，平台团队同时支持集团的 Agent Centre of Excellence。",
          "他此前曾在 Maxis、Tigerspike、NE Digital 和 Kerry Logistics 任职，并在 Kerry Logistics 带领软件工程团队扩展至九十多人。2026 年 9 月，他开始全职担任安克极速首席执行官。",
          "在安克极速，Jazz 负责工程与 AI 项目交付，涵盖生产环境智能体、数据集成及定制软件。他在 Medium 撰文探讨 AgentOps、服务目录、以 AI 为核心的平台团队，以及企业把 AI 用于实际工作所需的改变。",
          "在工程职业生涯中，Jazz 构建的系统服务一千万月活跃用户，管理过超过一千台生产服务器，并处理过超过两百起事故。",
        ],
        topics: ["软件工程", "智能体工程", "平台工程", "AgentOps", "DevSecOps"],
      },
      {
        role: "联合创始人兼首席商务官",
        summary: "银行业、企业评估与实用 AI 应用。",
        paragraphs: [
          "Jee Fong 拥有超过十五年的企业银行、商业银行和业务转型经验。她曾在 Maybank、OCBC、RHB 任职，并在 Hong Leong Bank 和 CIMB 担任管理岗位。在 Hong Leong，她带领十二人团队；最近的银行职务是 CIMB 商业银行团队经理。",
          "在安克极速，她负责业务发展、客户合作及 Anchor Academy 的 AI 培训项目。她的工作包括与 Lenovo、Adobe 和 Microsoft 等技术供应商建立合作、与拉曼理工大学（TAR UMT）签署 MOU，以及为公司取得 Malaysia Digital 资格。",
          "作为伦敦政治经济学院（LSE）校友及 HRD Corp 认证培训师，她参与 AI 与生产力座谈、指导学生创业并评审商业提案。她的活动涵盖 HELP University、Taylor’s University、MMU Cyberjaya、INTI Nilai，以及 AI Build & Interiors Connect。",
          "在银行职业生涯中，Jee Fong 审查过十亿令吉的资本融资，并评估过超过五百家企业。",
        ],
        topics: ["企业评估", "融资评估", "企业与商业银行业务", "商业合作", "AI 培训与应用", "技术采购"],
      },
    ],
    medium: "阅读 Jazz 的 Medium 文章",
    contributionsHeading: "文章、座谈与创业指导。",
    writingHeading: "Jazz 的思考与写作",
    writingDescriptions: [
      "为何培训需要配合工作流程的改变，以及如何衡量实际效果。",
      "探讨智能体运行所需的指令和检查，以及它们如何影响表现。",
      "AI 辅助编程如何让开发者的工作从代码实现延伸至产品决策。",
    ],
    appearancesHeading: "Jee Fong 的社群参与",
    proofHeading: "公司资质与合作关系。",
    proof: [
      { label: "公司", detail: "成立于 2023 年 10 月 · 服务马来西亚与新加坡" },
      { label: "Malaysia Digital", detail: "获 MDEC 授予 Malaysia Digital 资格", href: "/about-us/" },
      { label: "AI 合作关系", detail: "Certified in the Claude Partner Network · OpenAI Select Partner", href: "/our-partners/", englishOnly: true },
      { label: "技术供应", detail: "授权经销商：Lenovo、ASUS、Dell、Adobe、Microsoft 及 Red Hat", href: "/enterprise/" },
      { label: "大学合作", detail: "与拉曼理工大学（TAR UMT）签署 MOU" },
      { label: "培训", detail: "由 Anchor Academy 提供 AI 培训项目", href: "/academy/" },
    ],
    approachEyebrow: "进一步了解",
    approachHeading: "我们的方法。",
    readingTitle: "为什么只有 AI 培训还不够",
    readingDescription: "了解我们对能力建设与工作流程重设计的看法。",
    academyTitle: "与 Anchor Academy 一起学习",
    academyDescription: "探索面向企业团队的实用培训。",
  },
};
